import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import StorageUtils from '@spothero/utils/storage';
import SHUtils from 'utils/sh-utils';
import Button from '@spothero/ui/button';
import TextButton from '@spothero/ui/text-button';
import Icon from 'common/icon/Icon';
import MultiRateChooser from './MultiRateChooser';

export default class ReservationDetailsDesktop extends PureComponent {
    static propTypes = {
        isMonthly: PropTypes.bool,
        isAdmin: PropTypes.bool,
        spot: PropTypes.object,
        starts: PropTypes.string,
        ends: PropTypes.string,
        isRebookingReservation: PropTypes.bool,
        onMultiRateChange: PropTypes.func,
        onTransientAvailableClick: PropTypes.func,
        onMonthlyAvailableClick: PropTypes.func,
        onWhyTimeExtendedClick: PropTypes.func,
        onRateDetailsClick: PropTypes.func,
        onOnlineCommuterRateClick: PropTypes.func,
        onVehicleRestrictionsClick: PropTypes.func,
        cancelAllSpotRequests: PropTypes.func,
        pushTo: PropTypes.func.isRequired
    };

    _onRateDetailsClick = () => {
        const {
            onRateDetailsClick
        } = this.props;

        if (onRateDetailsClick) {
            onRateDetailsClick('intro-rate');
        }
    }

    _onBookSpotClick = evt => {
        evt.preventDefault();

        const {
            spot: {
                distanceInMiles,
                selectedRate: {
                    fullUrl
                }
            },
            cancelAllSpotRequests,
            pushTo
        } = this.props;

        cancelAllSpotRequests();

        StorageUtils.set('spotDistanceinMiles', distanceInMiles);

        pushTo(fullUrl);
    }

    render() {
        const {
            isMonthly,
            isAdmin,
            spot: {
                facility: {
                    monthly_available: monthlyAvailable,
                    transient_available: transientAvailable
                },
                monthly_rates: monthlyRates,
                hourly_rates: hourlyRates,
                lowest_monthly_price: lowestMonthlyPrice,
                selectedRate,
                hasOversizedFee,
                vehicleRestriction
            },
            starts,
            ends,
            isRebookingReservation,
            onMultiRateChange,
            onTransientAvailableClick,
            onMonthlyAvailableClick,
            onWhyTimeExtendedClick,
            onRateDetailsClick,
            onOnlineCommuterRateClick,
            onVehicleRestrictionsClick
        } = this.props;
        const shortFormat = 'MM/DD/YYYY [at] h:mma';
        const longFormat = 'ddd, MMMM D [at] h:mma';
        const availableRates = (isMonthly) ? monthlyRates : hourlyRates;
        const rateStarts = moment(selectedRate.starts);
        const rateEnds = moment(selectedRate.ends);
        const duration = SHUtils.prettyDuration(rateEnds.diff(rateStarts));
        const timeExtended = (!isMonthly && (rateStarts < moment(starts) || rateEnds > moment(ends)));
        const lowestMonthlyPriceArr = (lowestMonthlyPrice)
            ? (lowestMonthlyPrice / 100)
                .toFixed(2)
                .toString('0.00')
                .replace(/\.0+$/, '')
                .split('.')
            : false;
        const lowestMonthlyPriceFormatted = (lowestMonthlyPriceArr) ? ` (starting at $${lowestMonthlyPriceArr[0]})` : '';
        const restrictionMessage = (hasOversizedFee && vehicleRestriction)
            ? 'Vehicle restrictions and fees.'
            : (hasOversizedFee)
                ? 'Oversized fee charged on site.'
                : (vehicleRestriction)
                    ? 'Vehicle restrictions apply.'
                    : null;
        const bookItClasses = classNames(
            'ReservationDetails-book-spot',
            {'admin-border-button': (isAdmin && isRebookingReservation)}
        );
        const bookItText = (isMonthly)
            ? 'Checkout'
            : (isRebookingReservation)
                ? 'Rebook It'
                : 'Book It';
        let monthlyStartsFormatted;

        if (isMonthly) {
            if (selectedRate.start_date_restrictions !== 'none') {
                const {
                    reservation_dates: reservationDates
                } = selectedRate;
                const invoiceStartDateTimeObj = moment(reservationDates[0].starts);
                const invoiceNextStartDateTimeObj = (reservationDates.length > 1) ? moment(reservationDates[1].starts) : null;

                if (!invoiceNextStartDateTimeObj) {
                    monthlyStartsFormatted = invoiceStartDateTimeObj.format('ddd, MMMM Do');
                } else {
                    monthlyStartsFormatted = `${invoiceStartDateTimeObj.format('MMM Do')} or ${invoiceNextStartDateTimeObj.format('MMM Do')}`;
                }
            } else {
                monthlyStartsFormatted = rateStarts.format('ddd, MMMM Do');
            }
        }

        return (
            <div className="ReservationDetails">
                {(availableRates.length > 1) &&
                    <span>
                        {!isMonthly &&
                            <p className="reservation-detail multi-rate">{rateStarts.format(shortFormat)} - {rateEnds.format(shortFormat)}</p>
                        }
                        <MultiRateChooser
                            selectedRate={selectedRate}
                            rates={availableRates}
                            onMultiRateChange={onMultiRateChange}
                            onInfoButtonClick={onRateDetailsClick}
                        />
                    </span>
                }
                {(availableRates.length === 1) &&
                    <span>
                        {!isMonthly &&
                            <span>
                                <p className="reservation-detail"><span className="label">Enter After:</span> {rateStarts.format(longFormat)}</p>
                                <p className="reservation-detail"><span className="label">Exit Before:</span> {rateEnds.format(longFormat)}</p>
                                <p className="reservation-detail"><span className="label">Total Time:</span> {duration}</p>
                            </span>
                        }
                        {isMonthly &&
                            <p className="reservation-detail">
                                <strong>
                                    {selectedRate.title}
                                    <TextButton
                                        onClick={onRateDetailsClick}
                                    >
                                        <Icon name="info" />
                                    </TextButton>
                                </strong>
                            </p>
                        }
                    </span>
                }
                {isMonthly &&
                    <span>
                        <p className="reservation-detail reservation-detail-monthly">
                            <span className="label">Month Begins:</span> {monthlyStartsFormatted}
                        </p>
                        {transientAvailable &&
                            <TextButton
                                className="see-transient-options"
                                onClick={onTransientAvailableClick}
                            >
                                <small>Hourly parking available</small>
                            </TextButton>
                        }
                    </span>
                }
                {!isMonthly &&
                    <span>
                        {monthlyAvailable &&
                            <span>
                                <TextButton
                                    className="see-monthly-options"
                                    onClick={onMonthlyAvailableClick}
                                >
                                    <small dangerouslySetInnerHTML={{__html: `Monthly parking available${lowestMonthlyPriceFormatted}`}} />
                                </TextButton><br />
                            </span>
                        }
                        {timeExtended &&
                            <TextButton
                                className="why-different-times"
                                onClick={onWhyTimeExtendedClick}
                            >
                                <small>Why is my time extended for free?</small>
                            </TextButton>
                        }
                    </span>
                }
                <div className="actions">
                    {!selectedRate.unavailable &&
                        <span>
                            <h4
                                className="ReservationDetails-total-price"
                                dangerouslySetInnerHTML={{__html: `${selectedRate.price_formatted} Total`}}
                            />
                            {(isMonthly && selectedRate.introductory && selectedRate.introductory.rate) &&
                                <small className="intro-rate">
                                    Intro Rate.{' '}
                                    <TextButton
                                        data-section="intro-rate"
                                        onClick={this._onRateDetailsClick}
                                    >
                                        Details
                                    </TextButton>
                                </small>
                            }
                            {!isMonthly &&
                                <span>
                                    {selectedRate.online_commuter_rate &&
                                        <small className="online-commuter-rate">
                                            Online Special.{' '}
                                            <TextButton
                                                onClick={onOnlineCommuterRateClick}
                                            >
                                                Details
                                            </TextButton>
                                        </small>
                                    }
                                    {restrictionMessage &&
                                        <small className="oversize-fee">
                                            {restrictionMessage}&nbsp;
                                            <TextButton onClick={onVehicleRestrictionsClick}>
                                                Details
                                            </TextButton>
                                        </small>
                                    }
                                </span>
                            }
                            <TextButton
                                className={bookItClasses}
                                color="primary"
                                onClick={this._onBookSpotClick}
                            >
                                {bookItText}
                            </TextButton>
                        </span>
                    }
                    {selectedRate.unavailable &&
                        <span>
                            {selectedRate.unavailable_reason &&
                                <span className="not-available-reason text-light"><sup>*</sup> {selectedRate.unavailable_reason}</span>
                            }
                            <Button
                                className="book-spot"
                                color="tertiary"
                                disabled
                            >
                                Unavailable <sup>*</sup>
                            </Button>
                        </span>
                    }
                </div>
            </div>
        );
    }
}
